import { DecisionGraph, iterateNodeElements } from "@imminently/decisively-core";
import {
  type VisibleMask,
  firstVisibleInstance,
  isShown,
  shownRef,
  shownSuccessors,
} from "@/components/GraphVisualisation/decisionGraphView";

export type GraphNode = {
  id?: string;
  path?: string;
  hidden?: boolean;
  description?: string;
  reference?: unknown;
  links?: unknown[];
  derived?: unknown;
  input?: unknown;
  value?: unknown;
};

export type ReportTreeNode = {
  type?: "special";
  title?: string;
  node?: GraphNode;
  hidden?: boolean;
  description?: string;
  reference?: unknown;
  links?: unknown[];
  expanded?: boolean;
  children?: ReportTreeNode[];
};

const ruleDetailKeys = new Set(["conditions", "otherwise", "rows", "columns", "value"]);

/** Strips rule-authoring detail (conditions/rows/etc) from a report tree, matching the "Download" (non-rules) export. */
export function stripRuleDetailFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripRuleDetailFields);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !ruleDetailKeys.has(key))
        .map(([key, item]) => [key, stripRuleDetailFields(item)]),
    );
  }

  return value;
}

/** Orders visible children by rule references and instance path, with duplicates removed. */
export function orderedSuccessors(graph: DecisionGraph, id: number, visible?: VisibleMask): number[] {
  const successors = shownSuccessors(graph, visible, id);
  const node = graph.node(id);
  if (!node || successors.length === 0) return successors;
  const elementIds = new Set<string>();
  iterateNodeElements(node as any, (element: any): undefined => {
    if (element?.attributeId) {
      elementIds.add(element.attributeId);
    }
    return undefined;
  });

  /**
   * the paths look like the following:
   *
   * pet/1/<guid 1>
   * pet/1/<guid 2>
   * pet/1/leash/1/<guid 1>
   * pet/1/leash/2/<guid 1>
   * pet/1/leash/3/<guid 1>
   * pet/1/leash/4/<guid 1>
   * pet/2/<guid 1>
   * pet/2/<guid 2>
   * pet/2/leash/1/<guid 1>
   * pet/2/leash/2/<guid 1>
   * pet/3/<guid 1>
   * pet/3/<guid 2>
   *
   * we need to sort by the pathing first and then by the ordering in elementIds
   */

  const elementIdArray = Array.from(elementIds);
  const paths = new Map<number, string>();
  for (const to of successors) paths.set(to, graph.deref(to));

  // Group by top-level path part, then by the order found in elementIds
  successors.sort((aId, bId) => {
    const a = paths.get(aId)!;
    const b = paths.get(bId)!;
    // Rule definition order is the primary order when both successors
    // correspond to attributes referenced by this rule.
    const aNodeId = graph.field(aId, "id") as string | undefined;
    const bNodeId = graph.field(bId, "id") as string | undefined;
    const idxA = elementIdArray.indexOf(aNodeId || a);
    const idxB = elementIdArray.indexOf(bNodeId || b);
    if (idxA !== -1 && idxB !== -1 && idxA !== idxB) return idxA - idxB;

    // get path segments
    const segA = a.split("/");
    const segB = b.split("/");

    const aPath = segA.slice(0, -1).join("/");
    const bPath = segB.slice(0, -1).join("/");

    if (aPath !== bPath) {
      // Group by base entity (e.g. pet/1 vs pet/1/leash/2, etc)
      const aPathParts = aPath.split("/");
      const bPathParts = bPath.split("/");
      for (let i = 0; i < Math.max(aPathParts.length, bPathParts.length); i++) {
        if (aPathParts[i] !== bPathParts[i]) {
          // Compare by path segment (numbers as numbers, others as string)
          const numA = Number(aPathParts[i]);
          const numB = Number(bPathParts[i]);
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
            return numA - numB;
          } else {
            return (aPathParts[i] || "").localeCompare(bPathParts[i] || "");
          }
        }
      }
    }

    // If paths are entirely equal, fall back to rule definition order.
    // Successors may be pathed instance ids, while rule elements store raw attribute ids.
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return 0;
  });

  const seenSuccessors = new Set<string>();
  const uniqueSuccessors = successors.filter((child) => {
    const key = (graph.field(child, "path") as string | undefined) || (graph.field(child, "id") as string | undefined) || paths.get(child)!;
    if (seenSuccessors.has(key)) return false;
    seenSuccessors.add(key);
    return true;
  });

  return uniqueSuccessors;
}

/**
 * Walks a decision graph from node `id` (only the nodes in the view), producing the
 * goal-rooted derivation tree consumed by the standalone Decision Lens timeline explorer
 * (docs/prototype/template.html) and rendered by DecisionReport.tsx. Nodes already visited on
 * the current path are collapsed into a `{ type: "special", title: "Proven elsewhere" }` cross-edge
 * rather than re-descended, to keep cyclic/shared derivations from producing an infinite tree.
 */
export function recurseDecisionReportNode(
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  id: number,
  seen: Set<number> = new Set(),
): ReportTreeNode | undefined {
  const node = isShown(visible, id) ? (graph.node(id) as GraphNode | undefined) : undefined;
  if (!node || node.hidden === true) return undefined;
  if (seen.has(id)) {
    const children = shownSuccessors(graph, visible, id);
    if (children.length === 0) return { node, children: [] };
    return {
      node,
      children: [
        {
          type: "special",
          title: "Proven elsewhere",
          node,
        },
      ],
    };
  }
  seen.add(id);

  const children = orderedSuccessors(graph, id, visible)
    .map(child => recurseDecisionReportNode(graph, visible, child, seen))
    .filter((x): x is ReportTreeNode => Boolean(x));

  return {
    node,
    title: node.description,
    expanded: true,
    children,
  };
}

/**
 * Builds the goal-rooted decision report tree (as an array with a single root, matching the shape
 * DecisionReport.tsx downloads and template.html's ingestReport() expects) for a given graph + goal.
 */
export function buildDecisionReportTree(
  source: DecisionGraph,
  goal: string,
  { includeRules = false, visible }: { includeRules?: boolean; visible?: VisibleMask } = {},
): ReportTreeNode[] {
  if (!goal || !source) return [];
  const graph = DecisionGraph.from(source);
  let goalId = shownRef(graph, visible, goal);
  const goalNode = goalId === undefined ? undefined : (graph.node(goalId) as GraphNode | undefined);
  if (goalNode?.hidden) {
    // `goal` isn't always a raw attribute id by the time it gets here — VisualisationsPanel.tsx
    // pre-resolves it to `goalNode.path || goalNode.id` before calling in. A hidden node is a
    // template/model shared across instances: start from its first visible instance instead (found
    // by the template's own `.id`, matching getRuleGraphFocusOptions in InvestigationsTab.tsx).
    const instanceModelId = goalNode.id;
    goalId = instanceModelId ? firstVisibleInstance(graph, visible, instanceModelId) : undefined;
  }

  const tree = (goalId === undefined ? [] : [recurseDecisionReportNode(graph, visible, goalId)]).filter(
    (x): x is ReportTreeNode => Boolean(x),
  );
  return includeRules ? tree : (stripRuleDetailFields(tree) as ReportTreeNode[]);
}
